import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {useState} from 'react';

import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {
    CertificationExamCarousel,
    VoucherUIRegion,
    type CertificationBundle,
} from '@udemy/react-certification-preparation';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {ContentArea} from '@udemy/react-layout-components';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';

import {PersonalPlanBanner} from 'src/components/personal-plan-banner';
import {CourseProvider} from 'src/hooks/certification_vouchers/useCourses';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {PageProps} from 'src/types/page-props';

import {CERT_VOUCHERS_SHOW_ADD_TO_CART} from '../../lib/experimentation-constants';
import {FrequentlyAskedQuestions} from './components/all-vouchers-faq/all-vouchers-faq.react-component';
import {getCertificationVouchersFAQ} from './components/all-vouchers-faq/faq-data';
import {Banner} from './components/banner/banner.react-component';
import {CourseUnitsWithProviders} from './components/course-units';

export type AllCertificationVouchersLandingPageFeatureProps = PageProps & {
    certificationBundle?: CertificationBundle | null;
};

export const AllCertificationVouchersLandingPageFeature: React.FC<
    AllCertificationVouchersLandingPageFeatureProps
> = (props) => {
    const [currentProvider, setCurrentProvider] = useState<CourseProvider>('all');
    const {gettext} = useI18n();
    const router = useRouter();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);
    const {featureVariantAssignments} = useFeatures([CERT_VOUCHERS_SHOW_ADD_TO_CART]);

    const certVouchersFeature = featureVariantAssignments.get(CERT_VOUCHERS_SHOW_ADD_TO_CART);
    const certVouchersShowAddToCartButton =
        certVouchersFeature?.configuration?.certVouchersShowAddToCart ?? false;

    const handleProviderChange = (providerId: string) => {
        setCurrentProvider(providerId as CourseProvider);
    };

    const metaProps = {
        title: gettext('Udemy Cert Prep + Exam Vouchers'),
        description: gettext(
            'Turn prep into success with Udemy. Get expert-led training and save 10% when booking your certification exam voucher.',
        ),
    };

    const certificationVouchersFAQ = getCertificationVouchersFAQ(gettext);

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.title}
                description={metaProps.description}
                ogDescription={metaProps.description}
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[props.locale]}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <FunnelLogContextProvider pageType="allVouchers">
                <SubscriptionPlansProvider
                    subscriptionPlanQueryVariables={[
                        {
                            filters: {
                                productType: SubscriptionPlanProductType.Consumersubscription,
                            },
                            marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                        },
                    ]}
                >
                    <Banner />
                    <ContentArea className={classNames('ud-container')}>
                        <CertificationExamCarousel
                            uiRegion={'voucher_landing' as VoucherUIRegion}
                            title={gettext('Get your exam vouchers and save')}
                            titleClassName="ud-text-bold"
                            onProviderChange={handleProviderChange}
                            showAddToCart={certVouchersShowAddToCartButton}
                            experimentFeature={certVouchersFeature}
                        />

                        <CourseUnitsWithProviders
                            provider={currentProvider}
                            certificationBundle={props.certificationBundle}
                        />

                        <PersonalPlanBanner />

                        <div>
                            <FrequentlyAskedQuestions
                                questionsAndAnswers={certificationVouchersFAQ}
                            />
                        </div>
                    </ContentArea>
                </SubscriptionPlansProvider>
            </FunnelLogContextProvider>
        </>
    );
};
